import type {
  ApiContext,
  ApiNotAuthorized,
  CardDetail,
  CardEdit,
  CardEditResult,
  CardIdentityTaken,
  CardListQuery,
  CardNotFound,
  CardPage,
  CardPricePoint,
  CardStateRefused,
  CardStockUpdate,
  InvalidFormField,
  StockLevel,
  StoreNotFound,
  StoreRoleForbidden,
} from "@hobbyshopify/backend-client";
import type { AsyncResult } from "operation-result.js";
import type { AuthHandler } from "trunk.js";
import type { BackendApi } from "./api";

/** The tab strip's vocabulary; `all` is the absent filter, not a value the backend stores. */
export type CardListFilter = NonNullable<CardListQuery["filter"]>;

type Api = { client: BackendApi; authHandler: AuthHandler<ApiContext> };

/** Cards a page of the Stock list asks for, as iOS asks. */
export const CARDS_PAGE_SIZE = 30;
/** The most cards the server answers in one page. */
export const CARDS_PAGE_MAX = 250;

/** Which kinds of item the catalogue shows. */
export type CatalogueKind = NonNullable<CardListQuery["kind"]>;

/** One page of the catalogue; `cursor` asks for the page after a previous answer's `nextCursor`. */
export function loadCards(
  o: Api & {
    shopId: string;
    filter: CardListFilter;
    kind: CatalogueKind;
    search: string;
    cursor?: string;
    limit?: number;
  },
): AsyncResult<CardPage, ApiNotAuthorized | StoreNotFound> {
  return o.authHandler.callApi((ctx) =>
    o.client.cards.list(ctx, o.shopId, {
      filter: o.filter,
      kind: o.kind,
      search: o.search,
      limit: o.limit ?? CARDS_PAGE_SIZE,
      ...(o.cursor ? { cursor: o.cursor } : {}),
    }),
  );
}

export function loadCard(
  o: Api & { shopId: string; cardId: string },
): AsyncResult<CardDetail, ApiNotAuthorized | StoreNotFound | CardNotFound> {
  return o.authHandler.callApi((ctx) => o.client.cards.get(ctx, o.shopId, o.cardId));
}

/**
 * Corrects what a card is. The answer says whether it became a different card,
 * whose pricing product and comps were cleared, so its caller reprices it.
 */
export function editCard(
  o: Api & { shopId: string; cardId: string; edit: CardEdit },
): AsyncResult<
  CardEditResult,
  | ApiNotAuthorized
  | StoreNotFound
  | CardNotFound
  | CardStateRefused
  | CardIdentityTaken
  | InvalidFormField
  | StoreRoleForbidden
> {
  return o.authHandler.callApi((ctx) => o.client.cards.edit(ctx, o.shopId, o.cardId, o.edit));
}

/** A fresh market lookup. Resolves to the new price in cents, or null when nothing was found. */
export function repriceCard(
  o: Api & { shopId: string; cardId: string },
): AsyncResult<number | null, ApiNotAuthorized | StoreNotFound | CardNotFound | StoreRoleForbidden> {
  return o.authHandler.callApi((ctx) => o.client.cards.reprice(ctx, o.shopId, o.cardId));
}

/** What the shop asks. Null takes the card off sale. */
export function setCardPrice(
  o: Api & { shopId: string; cardId: string; priceCents: number | null },
): AsyncResult<void, ApiNotAuthorized | StoreNotFound | CardNotFound | InvalidFormField | StoreRoleForbidden> {
  return o.authHandler.callApi((ctx) => o.client.cards.setPrice(ctx, o.shopId, o.cardId, o.priceCents));
}

/** What the shop will not go below. Null clears the floor. */
export function setCardFloor(
  o: Api & { shopId: string; cardId: string; floorPriceCents: number | null },
): AsyncResult<void, ApiNotAuthorized | StoreNotFound | CardNotFound | InvalidFormField | StoreRoleForbidden> {
  return o.authHandler.callApi((ctx) => o.client.cards.setFloor(ctx, o.shopId, o.cardId, o.floorPriceCents));
}

/** The new total on hand, or `trackStock: false` to stop counting. */
export function setCardStock(
  o: Api & { shopId: string; cardId: string; change: CardStockUpdate },
): AsyncResult<
  StockLevel,
  ApiNotAuthorized | StoreNotFound | CardNotFound | CardStateRefused | InvalidFormField | StoreRoleForbidden
> {
  return o.authHandler.callApi((ctx) => o.client.cards.setStock(ctx, o.shopId, o.cardId, o.change));
}

export function archiveCard(
  o: Api & { shopId: string; cardId: string },
): AsyncResult<void, ApiNotAuthorized | StoreNotFound | CardNotFound | CardStateRefused | StoreRoleForbidden> {
  return o.authHandler.callApi((ctx) => o.client.cards.archive(ctx, o.shopId, o.cardId));
}

/** The card's recorded prices, oldest first. */
export function loadPriceHistory(
  o: Api & { shopId: string; cardId: string },
): AsyncResult<CardPricePoint[], ApiNotAuthorized | StoreNotFound> {
  return o.authHandler.callApi((ctx) => o.client.cards.prices(ctx, o.shopId, o.cardId));
}
